<template>
  <div
    v-if="visible"
    class="graveyard-overlay"
    @click.self="$emit('close')"
    @touchstart.stop
    @mousedown.stop
  >
    <div class="graveyard-panel">
      <div class="graveyard-header">
        <h2>{{ title }}</h2>
        <span class="graveyard-count">{{ t('duel.gyCount', { n: cards.length }) }}</span>
        <button class="graveyard-close" @click="$emit('close')">&#10005;</button>
      </div>
      <div class="graveyard-list">
        <div
          v-for="(card, index) in reversedCards"
          :key="index"
          class="graveyard-card"
          :class="{
            'graveyard-card-selectable': selectable,
            'fusion-ready': fusionReady(card),
          }"
          @mouseenter="$emit('hover', card)"
          @click="selectable ? $emit('select', card) : $emit('hover', card)"
        >
          <div class="graveyard-card-img">
            <CardArt
              v-if="card.id"
              :card-id="card.id"
              :foil="card.foil"
              :alt="localizedName(card.id, card.name)"
              intensity="thumb"
              immediate
            />
          </div>
          <div class="graveyard-card-info">
            <div class="graveyard-card-name">{{ localizedName(card.id, card.name) }}</div>
            <div class="graveyard-card-type">{{ card.type ? t(`card.type.${card.type}`) : '' }}</div>
            <div v-if="card.atk != null" class="graveyard-card-stats">
              ATK {{ card.atk }} / DEF {{ card.def }}
            </div>
            <div
              v-if="card.desc && card.frameType === 'fusion'"
              class="graveyard-card-desc"
              :class="{ 'is-marked': availableCardNames }"
            >
              <template v-if="availableCardNames">
                <span
                  v-for="(part, pi) in formatMaterials(card)"
                  :key="pi"
                  :class="part.state === 'plain' ? undefined : `material-${part.state}`"
                >{{ part.text }}</span>
              </template>
              <template v-else>{{ fusionDesc(card) }}</template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '../i18n/t'
import CardArt from './CardArt.vue'
import {
  localizedDesc,
  localizedName,
  localizedNameFromEnglish,
} from '../i18n/localizeCard'
import {
  fusionMaterialsReady,
  splitFusionText,
  type FusionTextPart,
} from '../game/fusionMaterialText'

const { t } = useI18n()

const props = defineProps<{
  visible: boolean
  cards: any[]
  owner: string
  customTitle?: string
  availableCardNames?: string[]
  selectable?: boolean
}>()

defineEmits<{
  close: []
  hover: [card: any]
  select: [card: any]
}>()

const title = computed(() => {
  if (props.customTitle) return props.customTitle
  return props.owner === 'player' ? t('duel.gyYours') : t('duel.gyOpp')
})

const reversedCards = computed(() => [...props.cards].reverse())

function fusionDesc(card: { id?: string | number; desc?: string }): string {
  return card.id != null
    ? localizedDesc(card.id, card.desc ?? '')
    : (card.desc ?? '')
}

function formatMaterials(card: {
  id?: string | number
  desc?: string
  frameType?: string
}): FusionTextPart[] {
  const desc = fusionDesc(card)
  const available = (props.availableCardNames ?? []).map(localizedNameFromEnglish)
  return splitFusionText(desc, available)
}

function fusionReady(card: {
  id?: string | number
  desc?: string
  frameType?: string
}): boolean {
  if (!props.availableCardNames || card.frameType !== 'fusion') return false
  return fusionMaterialsReady(formatMaterials(card))
}
</script>

<style scoped>
.graveyard-overlay {
  position: fixed;
  top: 0;
  right: var(--viewer-rail-end, 0px);
  bottom: 0;
  left: var(--viewer-rail, 400px);
  background: rgba(0, 0, 0, 0.7);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.graveyard-panel {
  background: #1a1a2e;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  width: min(500px, calc(100% - 24px));
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
}

@media (max-height: 500px) {
  .graveyard-overlay {
    left: var(--viewer-rail, 0px);
    right: var(--viewer-rail-end, 0px);
  }

  .graveyard-panel {
    width: min(560px, calc(100% - 16px));
    max-height: calc(100vh - 16px);
  }
}

.graveyard-header {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.graveyard-header h2 {
  color: #ffffff;
  font-size: 18px;
  margin: 0;
  flex: 1;
}

.graveyard-count {
  color: #9ca3af;
  font-size: 14px;
  margin-right: 16px;
}

.graveyard-close {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.graveyard-close:hover {
  color: #ffffff;
}

.graveyard-list {
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.graveyard-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.graveyard-card:hover {
  background: rgba(255, 255, 255, 0.08);
}

.graveyard-card-selectable:hover {
  background: rgba(74, 222, 128, 0.15);
  border: 1px solid rgba(74, 222, 128, 0.4);
}

.graveyard-card-img {
  width: 40px;
  height: 58px;
  border-radius: 4px;
  overflow: visible;
  flex-shrink: 0;
  background: #2a2a3e;
}

.graveyard-card-img :deep(.card-art) {
  width: 100%;
  height: 100%;
  border-radius: 4px;
}

.graveyard-card-info {
  flex: 1;
  min-width: 0;
}

.graveyard-card-name {
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.graveyard-card-type {
  color: #9ca3af;
  font-size: 12px;
}

.graveyard-card-stats {
  color: #facc15;
  font-size: 12px;
}

.graveyard-card.fusion-ready {
  box-shadow: inset 3px 0 0 #4ade80;
  background: rgba(74, 222, 128, 0.1);
}

.graveyard-card.fusion-ready .graveyard-card-name {
  color: #4ade80;
}

.graveyard-card-desc {
  font-size: 11px;
  font-style: italic;
  margin-top: 2px;
  color: #9ca3af;
}

.graveyard-card-desc.is-marked {
  font-size: 0;
}

.graveyard-card-desc.is-marked span {
  font-size: 11px;
  font-style: italic;
}

.material-available {
  color: #4ade80;
  font-weight: 700;
  font-style: normal;
}

.material-missing {
  color: #f87171;
}

.graveyard-list::-webkit-scrollbar {
  width: 5px;
}

.graveyard-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.graveyard-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}
</style>
